//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { ArrowRight } from "@mui/icons-material";
import {
    Modal,
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
    Typography,
} from "@mui/material";
import GradeDisplay from "components/GradeDisplay";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import {
    get,
    has,
    map
} from "lodash";
import { lighten } from "polished";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useCurrentUser } from "store/users/selectors";
import { handleListQuestionOfWorksheet } from "store/worksheet/thunks";
import styled from "styled-components";
import { getStatusByCode } from "utils/lib/ebookHelper";
import { checkIsManualGradable, isGradedByTeacher } from "utils/methods/math";
import closeIconSvg from "../../utils/assets/images/close-icon.svg";
import SentryWrap from 'components/Sentry';
import { normalizeWorksheetQuestions } from "utils/methods/functions";

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------

// ✅ Accordion
const Accordion = styled(MuiAccordion)(({ theme }) => ({
  border: "1px solid white",
  boxShadow: "none",
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "auto",
  },
}));

// ✅ AccordionSummary
const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: "#03989e",
  color: "white",
  textTransform: "capitalize",
  borderBottom: "1px solid white",
  marginBottom: -1,
  minHeight: 47,
  height: 47,
  "&.Mui-expanded": {
    minHeight: 47,
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    margin: "8px 0",
  },
}));

// ✅ AccordionDetails
const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: "2px",
  display: "flex",
  flexDirection: "column",
}));

const AccordianTitle = styled(Typography)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding-right: 14px;
  gap: 10px;
  align-items: center;
`;
const Rotate = styled.div`
  align-items: center;
  display: flex;
  height: 16px;
  justify-content: center;
  margin-right: 4px;
  transform: ${(p) => (p.open ? "rotate(90deg)" : "rotate(0deg)")};
  transition: all 300ms ease;
  width: 16px;
`;

const Notification = styled.div`
  background-color: ${(p) =>
    p.bgColor ? p.bgColor : lighten(0.45, "#3D5F8F")};
  border: 1px dashed ${(p) => (p.bgColor ? p.color : "#3D5F8F")};
  border-radius: 3px;
  color: ${(p) => (p.color ? p.color : "#3D5F8F")};
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 24px;
  padding: 8px;
`;

const FeedbackLink = styled.div`
  border: 1px solid rgb(251, 251, 251);
  border-radius: 10px;
  padding: 4px 10px;
  color: #ffffff;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-right: 0px;
  margin-left: auto;
  font-size: 12px;
  font-weight: 800;
  transition: color 0.2s ease;
  opacity: ${props => props.disabled ? '0.5' : '1'};
`;

const CurrentSubmission = {
  backgroundColor: "#9933ff",
};
//  ----------------------------------------------------------------------------
//  Container
//  ----------------------------------------------------------------------------

export default function AssignmentResponse({
  responsesModalOpen,
  setResponsesModalOpen,
  submission,
  data,
  getCompleteInclompleteMark = () => {},
  isAssignmentTakable = () => {},
  showResponce
}) {
  const showAlert = useAlert();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setloadingQuestions] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const grade_type = get(data, "grade_type");
  const isPointSystem = grade_type === "points";
  const isCompleteInComplete = grade_type === "complete_incomplete";
  const ungraded = get(data, "ungraded", false);
  const max_point =
    data.max_point && !isNaN(data.max_point)
      ? Number.parseInt(data.max_point, 10)
      : 100;
  const is_game =
    submission?.assignmentType === "game" ||
    submission?.type === "game" ||
    data?.type === "game" ||
    false;
  const is_debate =
    submission?.assignmentType === "debate" || submission?.type === "debate";
  const is_worksheet =
    submission?.assignmentType === "worksheet" ||
    submission?.type === "worksheet";
  const is_loteria =
    submission?.assignmentType === "bingo" || submission?.type === "bingo";
  const is_data_debate = data?.type === "debate" || false;
  const is_data_loteria = data?.type === "bingo" || false;
  const is_data_worksheet = data?.type === "worksheet" || false;

  const handleChangeExpend = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : null);
  };

  const scrollToTeacherFeedback = (event, hasFeedback) => {
    event.stopPropagation();
    
      if (!hasFeedback) {
        showAlert('No teacher feedback available for this submission.', 'info');
        return;
      }
      setTimeout(() => {
        const expandedAccordions = document.querySelectorAll('.accordianModal .MuiAccordion-root.Mui-expanded');
        
        expandedAccordions.forEach((accordion) => {
          const accordionDetails = accordion.querySelector('.MuiAccordionDetails-root');
          if (accordionDetails) {
            const feedbackSection = accordionDetails.querySelector('#teacher-feedback-section');
            if (feedbackSection) {
              feedbackSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              });
            } else {
              accordionDetails.scrollTo({
                top: accordionDetails.scrollHeight,
                behavior: 'smooth',
              });
            }
          }
        });
      }, 300);
    };

  // Effects
  useEffect(() => {
    if (responsesModalOpen && submission?.id) {
      setExpanded(submission.id);
    }
  }, [responsesModalOpen, submission]);

  useEffect(() => {
    if (responsesModalOpen) {
      setExpanded("current");
    }
  }, [responsesModalOpen]);

  const fetchData = async (id) => {
    try {
      const has_q = questions.length>0
        setloadingQuestions(!has_q);
        const response = await dispatch(handleListQuestionOfWorksheet(id));
        const normalized = normalizeWorksheetQuestions(response);
        if (normalized.length > 0) {
            setQuestions(normalized);
        }else{
            setQuestions([]);
            showAlert("No questions found for this worksheet.", "warning");
        }
        
    } catch (error) {
        
    } finally{
        setloadingQuestions(false)
    }
  };

  useEffect(() => {
    if (!(is_worksheet || is_data_worksheet) || !responsesModalOpen) return;
    const worksheetId = data?.game_id ?? data?.data?.id;
    if (worksheetId) fetchData(worksheetId);
  }, [responsesModalOpen, is_worksheet, is_data_worksheet, data?.game_id, data?.data?.id]);

  const getGradePastSubmissions = (pt) => {
    const grade = pt?.grade > 0 || pt?.grade == 0 ? pt?.grade : 0;
    if(submission?.assignmentType === "bingo" && ungraded){
      const isSubmitted = submission?.answers.some((el)=> el?.is_submitted);
      return isSubmitted ? 'Credit' : '-';
    }
    if (ungraded) return "Credit";
    if (isCompleteInComplete) {
      return grade > 0 ? "Completed" : "In completed";
    } else if (isPointSystem) {
      if (grade > 0 && max_point > 0) {
        return `${Math.round((grade / 100) * max_point)}/${max_point}`;
      } else {
        return `0/${max_point}`;
      }
    }
    return `Grade -${grade}`;
  };

  const getGrade = () => {
    if (!submission || !submission.id) return "-";
    if (submission.retaking && !submission.complete && !is_worksheet && !is_data_worksheet) {
      return "in progress";
    }
    const grade = submission?.grade >= 0 ? submission.grade : "";
    const aiGradeExists = submission && submission.ai_grade && submission.ai_grade?.length > 0;
    let bg_text;
    if (currentUser?.role === 'student') {
      bg_text = 'TBG';
    } else {
      bg_text = aiGradeExists ? 'TBG - AI' : 'TBG';
    }
    const face_grade = submission?.face_grade;
    const isTbg =
      submission?.manualGrading &&
      checkIsManualGradable(get(submission, "assignment.questions"));
    if (ungraded) return "Credit";
    if (
      isCompleteInComplete &&
      (has(submission, "id") || !isAssignmentTakable(data))
    ) {
      return getCompleteInclompleteMark(submission?.grade > 0);
    }
    if (isPointSystem) {
      if (isTbg) {
        return face_grade
          ? `${face_grade}/${max_point} (${bg_text})`
          : `${bg_text}/${max_point}`;
      }
      return face_grade > 0 || face_grade == 0
        ? `${face_grade}/${max_point}`
        : `0/${max_point}`;
    } else {
      if (isTbg) {
        return grade == 0 || grade > 0 ? `${grade} (${bg_text})` : `${bg_text}`;
      }
      return grade == 0 || grade > 0 ? grade : "-";
    }
  };

  const pastSubmissions = useMemo(() => {
    if (!submission || !submission.pastSubmissions) return [];
    const returnedAtNumbers = Array.isArray(submission.returned_at)
      ? submission.returned_at.map((num) => parseInt(num) - 1)
      : [];
    const grouped = submission.pastSubmissions.reduce((acc, sub) => {
      const submissionNum = parseInt(sub.submissions);
      if (!acc[submissionNum]) {
        acc[submissionNum] = [];
      }
      acc[submissionNum].push(sub);
      return acc;
    }, {});

    const filtered = Object.values(grouped).flatMap((group) => {
      const hasNonEmptyAnswers = group.some(
        (sub) => Array.isArray(sub.answers) && sub.answers.length > 0
      );
      if (
        hasNonEmptyAnswers &&
        !returnedAtNumbers.includes(parseInt(group[0].submissions))
      ) {
        return group.filter(
          (sub) => Array.isArray(sub.answers) && sub.answers.length > 0
        );
      }
      return group;
    });
    const sorted = filtered.sort(
      (a, b) => parseInt(a.submissions) - parseInt(b.submissions)
    );
    return sorted;
  }, [submission]);

  const pastSubmissionView = useCallback(() => {
    if (pastSubmissions && pastSubmissions.length > 0) {
      return (
        <div>
          {map(pastSubmissions, (pt) => (
            <Accordion
              square
              expanded={expanded === pt.id}
              onChange={handleChangeExpend(pt.id)}
              className={expanded === pt.id ? "active" : ""}
            >
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
              >
                <AccordianTitle>
                  <span>
                    {"Submission-" + pt.submissions}{" "}
                    {getGradePastSubmissions(pt)} {isGradedByTeacher(pt, true)}
                  </span>
                {currentUser?.role !== 'parent' && expanded === pt.id && (
                  <FeedbackLink 
                    disabled={!(get(pt, "feedback") || get(pt, "audioFeedback"))}
                    onClick={(e) => scrollToTeacherFeedback(e, !!(get(pt, "feedback") || get(pt, "audioFeedback")))}
                  >
                    See teacher feedback
                  </FeedbackLink>
                )}
                  <Rotate open={expanded === pt.id}>
                    <ArrowRight />
                  </Rotate>
                </AccordianTitle>
              </AccordionSummary>
              <AccordionDetails>
                <GradeDisplay
                  key={get(pt, "id")}
                  answers={get(pt, "answers")}
                  feedback={get(pt, "feedback")}
                  ai_feedback={get(pt, "ai_feedback")}
                  ai_suggestion={get(pt, "ai_suggestion")}
                  audioFeedback={get(pt, "audioFeedback")}
                  grade={get(pt, "grade")}
                  name={get(submission, "displayName", get(submission, "assignment.displayName"))}
                  questions={get(submission, "assignment.questions", [])}
                  type={"past due"}
                  isLate={pt.islate}
                  isExpended
                  assignment={pt}
                  hasFeedback={
                    true && (get(pt, "feedback") || get(pt, "audioFeedback"))
                  }
                  activity={data}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      );
    } else {
      return null;
    }
  }, [pastSubmissions, expanded]);

  const CurrentSubmissionView = useCallback(() => {
    if (
      !(
        ((is_worksheet || is_data_worksheet) && submission?.id) ||
        get(submission, "submissions") != 0 ||
        get(submission, "graded") ||
        get(submission, "answers.length") > 0
      )
    ) {
      return null;
    }
      let PastSub = 0;
      map(pastSubmissions, (pt) => (PastSub = parseInt(pt.submissions)));
      const LastSub = PastSub ? PastSub : 0;
      const CurrentSub = LastSub + 1;
      let Title = "";
      if (CurrentSub === 1) {
        Title = CurrentSub;
      } else {
        Title = CurrentSub + " (Last Submission)";
      }
      const status = getGrade();
      const is_inprogress = status === "in progress";
      return (
        <div className="accordiaAssignment">
          <Accordion
            square
            expanded={expanded === "current"}
            onChange={handleChangeExpend("current")}
            className={expanded === "current" ? "active" : ""}
          >
            <AccordionSummary
              style={CurrentSubmission}
              aria-controls="panel1d-content"
              id="panel1d-header"
              className="assignAccordian"
            >
              <AccordianTitle>
                <span>
                  {"Submission-" + Title}{" "}
                  {` Grade - ${
                    get(submission, "exceptionStatus") > 0
                      ? getStatusByCode(
                          get(submission, "exceptionStatus"),
                          currentUser
                        )
                      : status
                  }`}{" "}
                  {isGradedByTeacher(submission)}
                </span>
              {currentUser?.role !== 'parent' && expanded === 'current' && (
                <FeedbackLink 
                  disabled={!(get(submission, "feedback") || get(submission, "audioFeedback"))}
                  onClick={(e) => scrollToTeacherFeedback(e, !!(get(submission, "feedback") || get(submission, "audioFeedback")))}
                >
                  See teacher feedback
                </FeedbackLink>
              )}
                <Rotate open={expanded === 'current'}>
                  <ArrowRight />
                </Rotate>
              </AccordianTitle>
            </AccordionSummary>
            <AccordionDetails>
              {is_inprogress ? (
                <Notification>
                  The assignment process is currently in progress.
                </Notification>
              ) : null}
              {currentUser?.role === "student" && is_inprogress && !(is_worksheet || is_data_worksheet) ? null : (
                <GradeDisplay
                  key={get(submission, "id")}
                  answers={get(submission, "answers")}
                  feedback={get(submission, "feedback")}
                  ai_feedback={get(submission, "ai_feedback")}
                  ai_suggestion={get(submission, "ai_suggestion")}
                  audioFeedback={get(submission, "audioFeedback")}
                  grade={
                    get(submission, "exceptionStatus") > 0
                      ? getStatusByCode(
                          get(submission, "exceptionStatus"),
                          currentUser
                        )
                      : getGrade()
                  }
                  // grade={get(submission, "exceptionStatus") > 0 ? getStatusByCode(get(submission, "exceptionStatus"),currentUser) : 0}
                  name={get(
                    submission,
                    "displayName",
                    get(submission, "assignment.displayName")
                  )}
                  questions={is_worksheet || is_data_worksheet ? questions : get(submission, "assignment.questions", [])}
                  // questions={
                  //   is_worksheet
                  //     ? []
                  //     : get(submission, "assignment.questions", [])
                  // }
                  selected_student_id={submission?.userId}
                  type={"current due"}
                  is_game={is_game}
                  is_debate={is_data_debate || is_debate}
                  is_worksheet={is_data_worksheet || is_worksheet}
                  is_loteria={is_data_loteria || is_loteria}
                  isLate={get(submission, "isLate")}
                  isExpended
                  assignment={submission}
                  activity={data}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </div>
      );
  }, [pastSubmissions, expanded, questions]);

    useEffect(() => {
      if ((!pastSubmissions || pastSubmissions.length === 0) && expanded !== 'current') {
        setExpanded('current');
      }
    }, [pastSubmissions]);
  

  return (
    <SentryWrap>
      <Modal
        open={responsesModalOpen}
      onClose={() => setResponsesModalOpen(false)}
      className="accordianModal"
    >
      <div className="ResponceContainerModal">
        <label
          className="accordiaAssigClos"
          onClick={() => setResponsesModalOpen(false)}
        >
          <img
            src={closeIconSvg}
            alt=""
          />
        </label>
        {pastSubmissionView()}
        {CurrentSubmissionView()}
      </div>
    </Modal>
    </SentryWrap>
  );
}
